import { AnimationSequencesType } from '../../utility/constants';
import { isPromise } from '../../utility/helper';
import { animationPromiseGenerator, AnimationGeneratorType, EffectType } from './animation-effects';

export type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] };
export type AnimationStartsCallbacks = {
  onAnimationStarts?: (() => Promise<unknown>) | (() => void);
};

export type AnimationEndsCallbacks = {
  onAnimationEnds?: (() => Promise<unknown>) | (() => void);
};

export type AnimationStatusCallbacks = AnimationStartsCallbacks & AnimationEndsCallbacks;

// eslint-disable-next-line @typescript-eslint/ban-types
export type SequenceIdentifier = AnimationSequencesType | (string & {});

export interface NaviAnimation {
  animation: EffectType & AnimationGeneratorType;
  elementSelector: string;
  onStart?: (() => Promise<unknown>) | (() => unknown);
  onEnd?: (() => Promise<unknown>) | (() => unknown);
}

export type SequenceStep = Array<NaviAnimation>;

export type AnimationSequence = {
  steps: Array<SequenceStep>;
  currentStepIndex: number;
  running: boolean;
  resolve?: (value: unknown) => void;
  reject?: (reason?: unknown) => void;
  promise?: Promise<void>;
};

const sequences: {
  [key in SequenceIdentifier]?: AnimationSequence | undefined;
} = {};
let rootElement: HTMLElement | null = null;

function initialize(element: HTMLElement): void {
  rootElement = element;
}

function register(sequenceIdentifier: SequenceIdentifier, animationSequence: AnimationSequence): void {
  sequences[sequenceIdentifier] = animationSequence;
}

function reset(): void {
  Object.keys(sequences).forEach((key) => delete sequences[key]);
}

async function play(sequenceIdentifier: SequenceIdentifier): Promise<unknown> {
  const sequence = sequences[sequenceIdentifier];

  if (sequence === undefined) {
    return new Promise((_, reject) => reject(new Error(`Sequence ${sequenceIdentifier} has not been registered`)));
  }

  if (!sequence.running) {
    sequence.promise = new Promise<void>((resolve, reject) => {
      sequence.resolve = () => {
        sequence.running = false;
        resolve();
      };

      sequence.reject = (value: unknown) => {
        sequence.running = false;
        reject(value);
      };

      sequence.running = true;

      applyCurrentAnimationSequenceStep(sequence);
    });
  }

  return sequence.promise;
}

function finishAnimationSequence(sequence: AnimationSequence) {
  sequence.currentStepIndex = 0;
  sequence.resolve?.(null);
  sequence.promise = undefined;
}

async function applyCurrentAnimationSequenceStep(sequence: AnimationSequence) {
  if (sequence.currentStepIndex >= sequence.steps.length) {
    finishAnimationSequence(sequence);
    return;
  }

  const sequenceStep = sequence.steps[sequence.currentStepIndex];

  const promises = [];
  for (const animation of sequenceStep) {
    promises.push(executeAnimation(animation));
  }

  try {
    await Promise.all(promises);
  } catch (error) {
    sequence.reject?.(error);
    return;
  }

  sequence.currentStepIndex += 1;

  setTimeout(() => applyCurrentAnimationSequenceStep(sequence), 0);
}

async function executeAnimation({ onStart, onEnd, animation, elementSelector }: NaviAnimation): Promise<void> {
  if (rootElement === null) {
    throw new Error('No root element present');
  }

  if (onStart) {
    const maybePromise = onStart();
    if (isPromise<void>(maybePromise)) {
      await maybePromise;
    }
  }

  const animatedElements: HTMLElement[] = Array.from(rootElement.querySelectorAll(elementSelector));
  const animationExecutors = [];

  for (const element of animatedElements) {
    const animationPromiseEffect = animationPromiseGenerator(element, animation);
    animationExecutors.push(animationPromiseEffect);
  }

  await Promise.all(animationExecutors);

  if (onEnd) {
    const maybePromise = onEnd();
    if (isPromise<void>(maybePromise)) {
      await maybePromise;
    }
  }
}

function isPlaying(sequenceIdentifier: SequenceIdentifier): boolean {
  const sequence = sequences[sequenceIdentifier];

  if (sequence === undefined) {
    throw new Error(`Sequence ${sequenceIdentifier} has not been registered`);
  }

  return sequence.promise !== undefined;
}

function makeSequence(
  sequenceSteps: SequenceStep[],
  animationDefaults?: Partial<Omit<AnimationGeneratorType & EffectType, 'name'>>
): AnimationSequence {
  return {
    steps: sequenceSteps.map((step) => step.map((animation) => ({ ...animationDefaults, ...animation }))),
    currentStepIndex: 0,
    running: false
  };
}

export default {
  initialize,
  play,
  isPlaying,
  register,
  reset
};

export { makeSequence };
